package top.wcpe.allincore.ui.runtime.render

import top.wcpe.allincore.layout.engine.Box
import top.wcpe.allincore.ui.runtime.element.ItemStack
import top.wcpe.allincore.ui.runtime.element.UIElement
import top.wcpe.allincore.ui.runtime.render.UiRenderBridge
import top.wcpe.allincore.ui.runtime.theme.ThemeAccess

/**
 * 物品堆栈渲染器：绘制背景框 + 物品图标 + hover 高亮。
 *
 * 当 [ItemStack.showBackground] 为 true 时，先画背景框再画物品；hover 时叠加高亮层。
 * 颜色优先级：元素覆盖 > 主题 token（itemStackBackgroundColor / itemStackBackgroundInnerColor /
 * itemStackHoverColor）。tooltip 由通用 tooltip 系统自动处理。
 *
 * count 角标在平台层绘制，`badge*` 延后。
 *
 * flush 契约（v0.20.0）：三段绘制分别归属 BACKGROUND / ITEM / OVERLAY 三个渲染层，
 * 由注册表以 [top.wcpe.allincore.ui.runtime.render.layer.LayerRenderers] 组合并在层边界
 * 统一 flush；本类保持单遍绘制语义，供不经层缓冲的直绘路径使用。
 */
@Suppress("MagicNumber")
class ItemStackRenderer : Renderer {

    override fun render(element: UIElement<*>, box: Box, bridge: UiRenderBridge) {
        val item = element as ItemStack
        ItemStackRenderParts.renderBackground(item, box, bridge)
        ItemStackRenderParts.renderItem(item, box, bridge)
        ItemStackRenderParts.renderOverlay(item, box, bridge)
    }
}

/** ItemStack 背景段（BACKGROUND 层）。 */
internal class ItemStackBackgroundRenderer : Renderer {
    override fun render(element: UIElement<*>, box: Box, bridge: UiRenderBridge) {
        ItemStackRenderParts.renderBackground(element as ItemStack, box, bridge)
    }
}

/** ItemStack 物品图标段（ITEM 层）。 */
internal class ItemStackItemRenderer : Renderer {
    override fun render(element: UIElement<*>, box: Box, bridge: UiRenderBridge) {
        ItemStackRenderParts.renderItem(element as ItemStack, box, bridge)
    }
}

/** ItemStack 状态叠加段（OVERLAY 层）。 */
internal class ItemStackOverlayRenderer : Renderer {
    override fun render(element: UIElement<*>, box: Box, bridge: UiRenderBridge) {
        ItemStackRenderParts.renderOverlay(element as ItemStack, box, bridge)
    }
}

@Suppress("MagicNumber")
private object ItemStackRenderParts {

    fun renderBackground(item: ItemStack, box: Box, bridge: UiRenderBridge) {
        if (!item.showBackground()) return
        val theme = ThemeAccess.current()
        val borderWidth = item.backgroundBorderWidth() ?: 1f
        val outerColor = item.backgroundColor() ?: theme.itemStackBackgroundColor
        val innerColor = item.backgroundInnerColor() ?: theme.itemStackBackgroundInnerColor
        bridge.fillRect(box.x(), box.y(), box.width(), box.height(), outerColor)
        bridge.fillRect(
            box.x() + borderWidth,
            box.y() + borderWidth,
            box.width() - borderWidth * 2,
            box.height() - borderWidth * 2,
            innerColor,
        )
    }

    fun renderItem(item: ItemStack, box: Box, bridge: UiRenderBridge) {
        val itemId = item.itemId()
        if (itemId.isEmpty()) return
        val theme = ThemeAccess.current()
        val borderWidth = item.backgroundBorderWidth() ?: 1f
        val inset = if (item.showBackground()) borderWidth else 0f
        // 背景遮罩色（issue #68）：仅在画了背景框时，物品背后才是内层纯色 bgInnerColor；
        // 无背景框（showBackground=false）时背后非纯色，传 0 让平台层回退旧淡化路径。
        val backdropColor = if (item.showBackground()) {
            item.backgroundInnerColor() ?: theme.itemStackBackgroundInnerColor
        } else {
            0
        }
        // RFC-RECOMPOSITION：用带 enchanted 标志的重载，让平台层叠加 vanilla foil/glint 流光
        // 而不需要修改 ItemStack NBT。改用携带数量锚点/偏移的规范重载：
        // 默认 countAnchor="bottom-right" + 零偏移复刻 vanilla 右下角数量徽标，行为不变。
        // 按 customNbt 生成 SNBT 透传，使平台层解析自定义 bbmodel/贴图。
        bridge.drawItemStack(
            itemId,
            StaticItemNbt.snbtOfCached(item),
            item.count(),
            item.enchanted(),
            true,
            item.countAnchor(),
            item.countOffsetX() ?: 0f,
            item.countOffsetY() ?: 0f,
            backdropColor,
            box.x() + inset,
            box.y() + inset,
            box.width() - inset * 2,
            box.height() - inset * 2,
        )
    }

    fun renderOverlay(item: ItemStack, box: Box, bridge: UiRenderBridge) {
        if (!item.hovered()) return
        val hoverColor = item.hoverOverlayColor() ?: ThemeAccess.current().itemStackHoverColor
        bridge.fillRect(box.x(), box.y(), box.width(), box.height(), hoverColor)
    }
}
